import React, { useMemo } from 'react';
import { Segment } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import { DBO_TM } from '@op/utils/format';
import { GAME_CATEGORY } from '@opm/components/FilterCommon/constants/games';
import { IFilter } from '../../types/filter';
import { IAnchorMeta, TAnchor } from '../../types/common';
import { useGameColorContext } from '../../context/GameColorContext';
import './index.scss';
import { DefaultImg } from '@op/constants/defaultImg';
import RenderImg from '@op/components/RenderImg';

export interface IStickyNavigatorProps {
  /** 锚点列表 */
  anchors: IAnchorMeta[];
  /** 当前激活的锚点 */
  active: TAnchor;
  /** 点击锚点回调 */
  onAnchorClick: (anchor: TAnchor) => void;
  /** 全局筛选值（用于右侧回显） */
  filter: IFilter;
  /** 是否可见（受吸顶状态控制） */
  visible: boolean;
}

/**
 * StickyNavigator · 吸顶导航条
 *
 * 需求 §4.4.2 + D6：
 * - 左侧：可点击 Segment，四大模块锚点切换（严格使用模块名，禁用占位团队名）
 * - 右侧：当前筛选值只读回显（品类 / 游戏 / 时间），不可点击
 *
 * 参考 Figma：3273-35688 / design_sticky_header.png
 */
export default function StickyNavigator({
  anchors,
  active,
  onAnchorClick,
  filter,
  visible,
}: IStickyNavigatorProps) {
  const { intl } = useIntls();
  const { isEn } = useIntls();
  const { gameMap } = useGameColorContext();

  // Segment 选项（label 走 i18n）
  const segmentOptions = useMemo(
    () =>
      anchors.map((a) => ({
        value: a.key,
        text: intl(a.label),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [anchors, intl],
  );

  // 品类回显（与 FilterCommon/GameSelect 保持一致：
  // - 英文：直接展示 genre（即 filter.game_category 原值）
  // - 中文：从 cond materials_config_games 里取任一同 genre 的 genre_zh
  // - 特殊：All Games / Other Games 走固定中文映射）
  const categoryText = useMemo(() => {
    const category = filter.game_category as string;
    if (!category) return '';
    if (isEn) return category;

    // 特殊聚合项
    if (category === (GAME_CATEGORY.ALL as string)) return '全部游戏';
    if (category === (GAME_CATEGORY.OTHER_GAMES as string)) return '其他游戏';

    // 从 cond 数据里查找该 genre 的中文名
    if (gameMap) {
      const matched = Object.values(gameMap).find((g) => g?.genre === category);
      if (matched?.genre_zh) return matched.genre_zh;
    }
    return category;
  }, [filter.game_category, gameMap, isEn]);

  // 右侧 Filter 回显（从 gameMap 取游戏名 + icon；缺省时只展示数量）
  const gameContent = useMemo(() => {
    const ids = filter.game_ids || [];
    if (ids.length === 0) return <span>{intl('No games selected')}</span>;
    if (gameMap && ids.length <= 2) {
      return ids.map((id, index) => {
        const game = gameMap[id];
        if (!game) return null;
        return (
          <span key={id} className="dbom-competitor-ad-comparison-sticky__game-item">
            <RenderImg
              style={{ width: 20, height: 20, borderRadius: 4 }}
              defaultImage={DefaultImg.GAME}
              coverImage={game?.cover || ''}
            ></RenderImg>
            <span>{game.game_name}</span> {ids.length - 1 !== index ? ',' : ''}
          </span>
        );
      });
    }
    return <span>{String(ids.length)}</span>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.game_ids, gameMap, intl]);

  const timeText = useMemo(
    () =>
      DBO_TM.timeTitle({
        start_time: filter.start_time,
        end_time: filter.end_time,
        date_type: filter.date_type,
      }),
    [filter.start_time, filter.end_time, filter.date_type],
  );

  return (
    <div className={`dbom-competitor-ad-comparison-sticky ${visible ? 'dbom-competitor-ad-comparison-sticky--visible' : 'dbom-competitor-ad-comparison-sticky--hidden'}`}>
      <div className="dbom-competitor-ad-comparison-sticky__left">
        <Segment
          value={active}
          options={segmentOptions}
          onChange={(value) => onAnchorClick(value as TAnchor)}
        />
      </div>
      <div className="dbom-competitor-ad-comparison-sticky__right">
        <span className="dbom-competitor-ad-comparison-sticky__field">
          <span className="dbom-competitor-ad-comparison-sticky__label">
            {intl('Genre')}:
          </span>
          <span className="dbom-competitor-ad-comparison-sticky__value">
            {categoryText}
          </span>
        </span>
        <span className="dbom-competitor-ad-comparison-sticky__divider" />
        <span className="dbom-competitor-ad-comparison-sticky__field">
          <span className="dbom-competitor-ad-comparison-sticky__label">
            {intl('Games-1')}:
          </span>
          <span className="dbom-competitor-ad-comparison-sticky__value">
            {gameContent}
          </span>
        </span>
        <span className="dbom-competitor-ad-comparison-sticky__divider" />
        <span className="dbom-competitor-ad-comparison-sticky__field">
          <span className="dbom-competitor-ad-comparison-sticky__label">
            {intl('Date')}:
          </span>
          <span className="dbom-competitor-ad-comparison-sticky__value">
            {timeText}
          </span>
        </span>
      </div>
    </div>
  );
}
